import { HHTextField } from '@hinge-health/react-component-library';
import { InputAdornment } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { BillingModelType, PackageType } from '../../../../types';
import {
  extractMilestones,
  getFormErrorsForInnerField,
  innerFieldHasErrors,
} from '../../../../utils/contract-utils';
import { getCurrencySymbol } from '../../../../utils/currency-helpers';
import { PackageFormData, ValidBillingModel } from '../custom-package-types';
import { fieldArrayName } from '../form';

export interface PriceProps {
  wholeFormDisabled: boolean | undefined;
  inputDisabled?: boolean;
  priceType: 'price' | 'initialFee' | 'activityFee';
  label: string;
  index: number;
  defaultPrice: number;
  packages: PackageType[];
  billingModels: BillingModelType[];
}

export const PriceInput = ({
  wholeFormDisabled,
  inputDisabled = false,
  priceType,
  label,
  index,
  defaultPrice,
  packages,
  billingModels,
}: PriceProps): JSX.Element => {
  const {
    control,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = useFormContext<PackageFormData>();

  /**
   * Tracks the initial render to prevent automatic price resets.
   * This is crucial for active and future contracts where we want to preserve
   * the existing price when loading the form, only updating it on explicit
   * billing model or package changes after the initial render.
   */
  const isInitialRender = useRef(true);

  const [
    watchedCurrentTemplate,
    watchedCurrency,
    watchedPackageId,
    watchedBillingModelId,
  ] = useWatch({
    name: [
      `${fieldArrayName}.${index}.currentTemplate`,
      'currency',
      `${fieldArrayName}.${index}.packageId`,
      `${fieldArrayName}.${index}.billingModelId`,
    ],
  });

  const isAcuteOnly =
    watchedPackageId &&
    packages
      .find(p => p.id === watchedPackageId)
      ?.packageOfferings.every(po => po.programIndication?.includes('acute'));

  const standardBillingModel = billingModels.find(
    bm => bm.name.toLowerCase() === ValidBillingModel.STANDARD,
  );

  const isStandardAcuteOnly =
    standardBillingModel?.id === watchedBillingModelId && isAcuteOnly;

  const milestonePriceTotal = extractMilestones(
    watchedCurrentTemplate.contract,
  ).reduce((total, { payment }) => total + payment, 0);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (priceType === 'price' && !wholeFormDisabled && !isStandardAcuteOnly) {
      setValue(`${fieldArrayName}.${index}.${priceType}`, defaultPrice, {
        shouldValidate: true,
      });
    } else if (
      priceType === 'price' &&
      !wholeFormDisabled &&
      isStandardAcuteOnly
    ) {
      setValue(`${fieldArrayName}.${index}.${priceType}`, 250, {
        shouldValidate: true,
      });
    }
  }, [
    watchedBillingModelId,
    watchedPackageId,
    priceType,
    wholeFormDisabled,
    setValue,
    index,
    defaultPrice,
    isStandardAcuteOnly,
  ]);

  useEffect(() => {
    if (
      priceType === 'price' &&
      milestonePriceTotal &&
      isDirty &&
      !wholeFormDisabled
    ) {
      setValue(`${fieldArrayName}.${index}.${priceType}`, milestonePriceTotal, {
        shouldValidate: true,
      });
    }
    trigger(`${fieldArrayName}.${index}.currentTemplate`);
  }, [
    milestonePriceTotal,
    trigger,
    index,
    priceType,
    setValue,
    isDirty,
    wholeFormDisabled,
  ]);

  return (
    <Controller
      control={control}
      name={`${fieldArrayName}.${index}.${priceType}`}
      render={({ field: { onChange, value } }): JSX.Element => (
        <HHTextField
          data-testid={`contract-${priceType}-input`}
          hhVariant="variant-bypass"
          label={label}
          disabled={wholeFormDisabled || inputDisabled}
          fullWidth
          key={`${priceType}-${index}`}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*',
            step: '0.01',
            min: '0',
            name: `${fieldArrayName}.${index}.${priceType}`,
            role: 'textbox',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                disableTypography
                sx={{ color: 'gray' }}
              >
                {getCurrencySymbol(watchedCurrency?.value)}
              </InputAdornment>
            ),
          }}
          error={innerFieldHasErrors(priceType, index, errors)}
          helperText={getFormErrorsForInnerField(priceType, index, errors)}
          value={value}
          onChange={onChange}
          type="number"
          InputLabelProps={{ sx: { color: 'gray' } }}
        />
      )}
    />
  );
};
